import React, { useEffect, useState } from "react";
import { Card, Button, Spin } from "antd";
import { LeftOutlined, RightOutlined } from "@ant-design/icons";
import { useDispatch } from "react-redux";
import ScheduleModal, { DailySchedule, ScheduleEvent } from "./ScheduleModal";
import { getData } from "../../../../../actions/apiCall";

/* -------------------- Types -------------------- */

interface Interview {
  _id: string;
  level: string;
  date: string;
  time: string;
  timeSortable: number;
  interviewerName: string[];
  candidateName: string;
}

interface CalendarDay {
  day: string;
  date: number;
  active?: boolean;
}

const getColorConfig = (level: string) => {
  const l = level.toLowerCase();
  // Purple theme (L1)
  if (l.includes("l1")) return { border: "bg-[#7148FC]", bg: "bg-[#F7F4FF]", dot: "border-[#7148FC]" };
  // Red theme (L2)
  if (l.includes("l2")) return { border: "bg-[#F34141]", bg: "bg-[#FFF2F2]", dot: "border-[#F34141]" };
  // Green theme (Salary/Discussion)
  if (l.includes("salary") || l.includes("discussion")) return { border: "bg-[#38C832]", bg: "bg-[#F1FBF0]", dot: "border-[#38C832]" };
  // Blue theme (Default/Other)
  return { border: "bg-[#3B82F6]", bg: "bg-[#F0F7FF]", dot: "border-[#3B82F6]" };
};

/* -------------------- Date Utils -------------------- */

const getStartOfWeek = (date: Date) => {
  const d = new Date(date);
  const day = d.getDay(); // Sunday = 0
  d.setDate(d.getDate() - day);
  d.setHours(0, 0, 0, 0);
  return d;
};

const getWeekDays = (
  weekStart: Date,
  selectedDate: Date
): CalendarDay[] => {
  const days: CalendarDay[] = [];

  for (let i = 0; i < 7; i++) {
    const d = new Date(weekStart);
    d.setDate(weekStart.getDate() + i);

    days.push({
      day: d.toLocaleString("en-US", { weekday: "short" }).toUpperCase(),
      date: d.getDate(),
      active: d.toDateString() === selectedDate.toDateString(),
    });
  }

  return days;
};

const formatApiDate = (date: Date) => {
  const options: Intl.DateTimeFormatOptions = {
    day: "2-digit",
    month: "short",
    year: "numeric",
  };
  return date.toLocaleDateString("en-GB", options);
};


const TodayInterviews: React.FC = () => {
  const dispatch = useDispatch();

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [interviews, setInterviews] = useState<Interview[]>([]);
  const [loading, setLoading] = useState(false);

  const [selectedDate, setSelectedDate] = useState(new Date());
  const [currentWeekStart, setCurrentWeekStart] = useState(
    getStartOfWeek(new Date())
  );


  const fetchInterviewsByDate = async (date: Date) => {
    setLoading(true);
    try {
      const formattedDate = formatApiDate(date);

      const res = await dispatch(
        getData(
          `/analytic/deshboard/todays/interviews/schedule?date=${formattedDate}`
        )
      );

      setInterviews(res?.data || []);
    } catch (error) {
      console.error("Failed to fetch interviews:", error);
      setInterviews([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchInterviewsByDate(selectedDate);
    setCurrentWeekStart(getStartOfWeek(selectedDate));
  }, [selectedDate]);


  const mapInterviewsToScheduleEvents = (
    data: Interview[]
  ): DailySchedule[] => {
    if (!data.length) return [];

    const grouped: Record<string, ScheduleEvent[]> = {};

    data.forEach((item, index) => {
      const event: ScheduleEvent = {
        id: index + 1,
        time: item.time,
        roundTitle: `Level: ${item.level} - ${item.candidateName}`,
        interviewerName: item.interviewerName.join(", "),
        colorKey: "purple",
      };

      if (!grouped[item.date]) grouped[item.date] = [];
      grouped[item.date].push(event);
    });

    return Object.keys(grouped).map((date) => ({
      date,
      events: grouped[date],
    }));
  };


  return (
    <>
      <Card
        className="rounded-xl"
        title={
          <div className="flex justify-between items-center pr-1">
            <span className="font-semibold text-gray-800">
              Today Interviews
            </span>
            <Button
              type="link"
              className="p-0 h-auto text-sm"
              onClick={() => setIsModalOpen(true)}
            >
              View All
            </Button>
          </div>
        }
      >
        {/* Selected Date */}
        <div className="text-sm text-gray-500 mb-4 -mt-3">
          {selectedDate.toLocaleString("en-US", {
            weekday: "short",
            day: "numeric",
            month: "short",
            year: "numeric",
          })}
        </div>

        {/* Week Calendar */}
        <div className="flex flex-col items-center border-b border-gray-200 pb-4">
          <div className="flex items-center justify-between w-full mb-3">
            <Button
              type="text"
              icon={<LeftOutlined />}
              onClick={() =>
                setCurrentWeekStart(
                  new Date(
                    currentWeekStart.getFullYear(),
                    currentWeekStart.getMonth(),
                    currentWeekStart.getDate() - 7
                  )
                )
              }
            />

            <span className="font-semibold text-base text-blue-600">
              {currentWeekStart.toLocaleString("en-US", {
                month: "long",
                year: "numeric",
              })}
            </span>

            <Button
              type="text"
              icon={<RightOutlined />}
              onClick={() =>
                setCurrentWeekStart(
                  new Date(
                    currentWeekStart.getFullYear(),
                    currentWeekStart.getMonth(),
                    currentWeekStart.getDate() + 7
                  )
                )
              }
            />
          </div>

          <div className="grid grid-cols-7 gap-1 w-full text-center">
            {getWeekDays(currentWeekStart, selectedDate).map((day, idx) => {
              const dateObj = new Date(currentWeekStart);
              dateObj.setDate(currentWeekStart.getDate() + idx);

              return (
                <div key={idx} className="flex flex-col items-center">
                  <p className="text-xs text-gray-500 uppercase">{day.day}</p>
                  <div
                    onClick={() => setSelectedDate(dateObj)}
                    className={`w-8 h-8 flex items-center justify-center rounded-lg mt-1 text-sm font-medium cursor-pointer
                      ${
                        day.active
                          ? "bg-blue-600 text-white"
                          : "text-gray-700 hover:bg-gray-100"
                      }`}
                  >
                    {day.date}
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Interview List */}
      <div className="mt-5 space-y-0">
  {loading ? (
    <div className="flex justify-center py-10"><Spin size="large" /></div>
  ) : interviews.length === 0 ? (
    <p className="text-center text-gray-500">No interviews scheduled.</p>
  ) : (
    interviews
      .sort((a, b) => a.timeSortable - b.timeSortable)
      .map((item, index) => {
        const colors = getColorConfig(item.level);
        const isLast = index === interviews.length - 1;

        return (
          <div key={item._id} className="flex min-h-[100px]">
            {/* Timeline Column */}
            <div className="flex flex-col items-center w-20 flex-shrink-0">
              <span className="text-[11px] font-bold text-gray-700 mb-1">
                {item.time}
              </span>
              {/* Hollow Circle Dot */}
              <div className={`w-3.5 h-3.5 rounded-full border-[2.5px] bg-white ${colors.dot} z-10`} />
              
              {/* Dashed Connector Line */}
              {!isLast && (
                <div className="w-0 flex-grow border-l-2 border-dashed border-gray-200 my-1" />
              )}
            </div>

            <div className={`flex-grow flex justify-between items-center p-4 mb-5 rounded-2xl relative overflow-hidden shadow-sm ${colors.bg}`}>
       
<div className={`absolute left-0 top-0 bottom-0 w-[6px] rounded-r-full ${colors.border}`} />
              
              <div className="pl-3">
                <h3 className="text-[15px] font-bold text-gray-800 leading-tight">
                  {item.level} : {item.candidateName}
                </h3>
                <p className="text-[12px] text-gray-500 mt-1">
                  Interviewer Name : <span className="text-gray-500">{item.interviewerName.join(", ")}</span>
                </p>
              </div>

              <button className="text-[#3B82F6] font-semibold text-sm underline underline-offset-4 hover:text-blue-700 px-2">
                Join
              </button>
            </div>
          </div>
        );
      })
  )}
</div>
      </Card>

      {/* Schedule Modal */}
      <ScheduleModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        scheduleData={mapInterviewsToScheduleEvents(interviews)}
      />
    </>
  );
};

export default TodayInterviews;
